import { Box, Button, Grid, TextField, Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { InvoiceProfileService } from "subModule/src/core/services";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import { useNavigate } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import CreditIcon from "@mui/icons-material/CreditScore";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useEffect, useRef, useState } from "react";
import Loader from "layout/Loader";
import DynamicSelect from "common/Select/Select";
import BasicDatePicker from "common/Pickers/Pickers";
import { useGetClientsDropdownService } from "subModule/src/services/client/useClient";
import dayjs from "dayjs";
import {
  GetInvoiceByIdResponse,
  GetInvoiceItemsByIdResponse,
  GetInvoicesByClientResponse,
} from "subModule/src/services/invoice/interface";
import { SalesCreditNoteTable } from "components/SalesCreditNotes/SalesCreditNoteTable";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import { useSelector } from "react-redux";
import AddIcon from "@mui/icons-material/Add";
import { selectUserInfo } from "features/user/userSlice";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";
interface FormData {
  [key: string]: any;
}
export default function SalesCreditNotesModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { breadcrumbItems } = useBusinessSetupEntityBreadcrumbs({
    sectionLabelKey: "customers",
    moduleLabelKey: "creditNotes",
    moduleHref: "/sales-credit-notes",
    entityFallbackLabelKey: "creditNote",
    isEdit: false,
  });
  const { enqueueSnackbar } = useSnackbar();
  const branding = useSelector(brandingInfo);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const loggedInUserCurrency = (useSelector(selectUserInfo) as any)?.business
    ?.currency;
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    getValues,
  } = useForm<FormData>({
    defaultValues: {
      creditNoteDate: dayjs(new Date())?.format("YYYY-MM-DD"),
      invoiceItem: [],
    },
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>("");
  const [pageNo, setPageNo] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const [invoiceloader, setInvoiceLoader] = useState<boolean>(false);
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(true);
  const [invoices, setInvoices] = useState<any>({});
  const [invoiceItem, setInvoiceItem] = useState<any[]>([]);
  const [deletedItems, setDeletedItems] = useState<any[]>([]);
  const [invoiceItemsLength, setInvoiceItemsLength] = useState(0);
  const [originalInvoiceItems, setOriginalInvoiceItems] = useState<any[]>([]);
  const [calculation, setCalculation] = useState<any>({});
  const { data: clientsData, refetch: clientsRefetch } =
    useGetClientsDropdownService({
      pageNumber: pageNo,
      size: 50,
      type: [160, 170, 180],
      search: dropdownSearchTerm,
    });
  const clients = (clientsData?.pages[0]?.data?.content || []).map(
    (item: { id?: number; name?: string }) => ({
      value: item.id,
      label: item.name,
    }),
  );
  useEffect(() => {
    clientsRefetch();
  }, []);
  const fetchInvoicesByClient = (
    clientId: number,
    currentPage: number,
    searchingText: string,
  ) => {
    setInvoiceLoader(true);
    InvoiceProfileService.getInvoicesByClient({
      page: currentPage,
      size: 50,
      clientId: clientId,
      invoiceNo: searchingText,
    })
      .then((response: GetInvoicesByClientResponse) => {
        if (response.success) {
          setInvoices(response?.data || {});
        } else {
          enqueueSnackbar(<AlertMessages text={response.message} />, {
            variant: "error",
          });
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        enqueueSnackbar(
          <AlertMessages
            text={err?.data?.message || err || "Something went wrong"}
          />,
          { variant: "error" },
        );
      })
      .finally(() => {
        setInvoiceLoader(false);
        setSearchLoading(null);
      });
  };
  const fetchInvoiceById = (invoiceId: number) => {
    InvoiceProfileService.getInvoiceByID(invoiceId)
      .then((res: GetInvoiceByIdResponse) => {
        if (res.success) {
          setValue("stationId", res?.data?.stationId);
          setValue("vehicleId", res?.data?.vehicleId);
          setValue("vehiclePlateNo", res?.data?.vehiclePlateNo);
          setValue("vehicleVin", res?.data?.vehicleVin);
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      })
      .finally(() => {
        setTimeout(() => fetchInvoiceItemsById(invoiceId), 700);
      });
  };
  const fetchInvoiceItemsById = (invoiceId: number) => {
    setLoading(true);
    InvoiceProfileService.getInvoiceItemsById(invoiceId)
      .then((res: GetInvoiceItemsByIdResponse) => {
        if (res.success) {
          const original = res.data;
          const mapped = original.map((item: any) => ({
            ...item,
            itemId: item.itemId ?? null,
            unitId: item?.unitId ?? null,
            unitTitle:
              item?.itemUnitResponse?.find(
                (option: any) => option.unitId === item?.unitId,
              )?.unitTitle ||
              item?.unitId ||
              "",
            quantity: item.quantity || 1,
            unitPrice: item.unitPrice || 0,
          }));
          setOriginalInvoiceItems(original);
          setInvoiceItemsLength(original.length);
          setDeletedItems([]);
          setInvoiceItem(mapped);
          setValue("invoiceItem", mapped);
          reCalc(original);
        } else {
          enqueueSnackbar(<AlertMessages text={res.message} />, {
            variant: "error",
          });
        }
      })
      .catch((err) => {
        enqueueSnackbar(<AlertMessages text={err?.message || "Error"} />, {
          variant: "error",
        });
      })
      .finally(() => setLoading(false));
  };
  const restoreDeletedItem = (itemId: number) => {
    setDeletedItems((prev) => prev.filter((item) => item.itemId !== itemId));
    reCalc(getValues());
  };
  const DeleteItem = (row: any, index: number) => {
    const filtered = invoiceItem.filter((_, i) => i !== index);
    setInvoiceItem(filtered);
    setValue("invoiceItem", filtered);
    setDeletedItems((prev) => {
      const alreadyExists = prev.some((item) => item.id === row.id);
      return row.id && !alreadyExists ? [...prev, row] : prev;
    });
    reCalc(getValues());
  };
  const addNewItem = () => {
    if (invoiceItem.length >= invoiceItemsLength) return;

    const remainingItems = originalInvoiceItems.filter(
      (original) => !invoiceItem.some((added) => added.id === original.id),
    );

    if (remainingItems.length === 0) return;

    const nextItem = remainingItems[0];

    const updated = [...invoiceItem, { ...nextItem }];
    setInvoiceItem(updated);
    setValue("invoiceItem", updated);
    reCalc(getValues());
  };
  const handleInputChange = (index: number, field: string, value: any) => {
    const updated = [...invoiceItem];
    updated[index][field] = value;
    setInvoiceItem(updated);
    setValue("invoiceItem", updated);
    reCalc(getValues());
  };
  const reCalc = (values: any) => {
    const ob = {
      subtotalAmount: 0,
      taxableAmount: 0,
      taxAmount: 0,
      discount: 0,
      totalAmount: 0,
      balanceAmount: 0,
      invoiceItem: [] as any,
    };

    (values.invoiceItem || values || []).forEach((row: any) => {
      // Use the manually entered price if available, otherwise calculate from base
      const effectiveUnitPrice =
        row?.unitPrice ||
        row?.baseUnitPrice *
          (row?.itemUnitResponse?.find((u: any) => u.unitId === row.unitId)
            ?.conversionMultiplier || 1);

      const amount = effectiveUnitPrice * (row?.quantity || 0);
      const tax = ((row?.taxPercentage || 0) * amount) / 100;
      const sub = amount;
      const dist = row?.discount || 0;
      const total = amount + tax - dist;

      ob.invoiceItem.push({
        ...row,
        unitPrice: effectiveUnitPrice,
        subtotalAmount: sub,
        taxableAmount: sub,
        taxAmount: tax,
        discount: dist,
        totalAmount: total,
        stockIn: row.stockIn,
      });

      ob.subtotalAmount += sub;
      ob.taxableAmount += sub;
      ob.taxAmount += tax;
      ob.discount += dist;
      ob.totalAmount += total;
    });
    setCalculation(JSON.parse(JSON.stringify(ob)) || {});
    setValue("invoiceItem", JSON.parse(JSON.stringify(ob.invoiceItem)));
    setInvoiceItem(JSON.parse(JSON.stringify(ob.invoiceItem)));
  };
  const onSubmit = (data: any) => {
    if (data?.invoiceItem?.length === 0) {
      enqueueSnackbar(<AlertMessages text={t("addAtLeastOneItem")} />, {
        variant: "error",
      });
      return;
    }
    setIsBtnEnabled(false);
    InvoiceProfileService.createSalesCreditNote({
      ...data,
      clientId: data.clientId.value,
      invoiceId: data.invoiceId.value,
    })
      .then((response: any) => {
        if (response.success) {
          enqueueSnackbar(
            <AlertMessages
              statusCode={response.status}
              text={
                t("creditNote") + " " + t("created") + " " + t("successfully")
              }
            />,
            { variant: "success" },
          );
          navigate(-1);
        } else {
          setIsBtnEnabled(true);
          enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
            variant: "error",
          });
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        setIsBtnEnabled(true);
        enqueueSnackbar(
          <AlertMessages
            text={err?.message || err?.data?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
      });
  };
  const handleDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => {
    if (action === "searchIt") {
      setPageNo(1);
      setDropdownSearchTerm(search);
      if (search) setSearchLoading(typeName);
    } else {
      setPageNo(pageNo + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (typeName === "customers")
        clientsRefetch().finally(() => setSearchLoading(null));
      else if (typeName === "invoices")
        fetchInvoicesByClient(
          watch("clientId")?.value,
          action === "loadNext" ? pageNo + 1 : 1,
          search || "",
        );
    }, 300);
  };
  return (
    <>
      <ComponentHeaderTwo
        breadcrumbItems={breadcrumbItems}
        showBtn={true}
        showSaveBtn={true}
        onClick={() => navigate(-1)}
        onSubmit={handleSubmit(onSubmit)}
        heading={t("addCreditNote")}
        icon={
          <CreditIcon
            sx={{
              fill: "#000",
              width: "30px",
              height: "30px",
              marginTop: "3px",
            }}
          />
        }
        btnText={"back"}
        btnType={"back"}
        isBtnEnabled={IsBtnEnabled}
        showCreateBtn={checkPrivileges("ROLE_CREDITNOTE_CREATE")}
      />
      <SimpleModal
        visable={true}
        padding={false}
        title={"addCreditNote"}
        isButtonVisable={true}
        isVisableBtn={IsBtnEnabled}
        btnclosetext="Back"
        buttonText={"submit"}
        attBtnNotshow={false}
        notVisableBackbtn={true}
        showPortList={handleSubmit(onSubmit)}
        formSubmit={handleSubmit(onSubmit)}
        content={
          <Grid
            container
            component="form"
            id="form"
            className="items-baseline px-2"
            spacing={2}
            onSubmit={handleSubmit(onSubmit)}
          >
            <Grid item xs={12} md={3}>
              <TextField
                {...register("referenceNumber")}
                label={t("referenceNo")}
                placeholder={t("phCreditNoteReferenceNo")}
                title={t("referenceNo")}
                fullWidth
                error={!!errors.referenceNumber}
                helperText={errors.referenceNumber?.message as string}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Controller
                name="creditNoteDate"
                control={control}
                rules={{
                  validate: (value) => {
                    const date = new Date(value);
                    return isNaN(date.getTime()) ? t("Invalid Date") : true;
                  },
                }}
                render={({ field }) => (
                  <BasicDatePicker
                    type="datePicker"
                    {...field}
                    label={t("creditNoteDate") + " *"}
                    fullWidth
                    onChange={(value: any) =>
                      field.onChange(
                        new Date(value)
                          .toLocaleDateString("en-CA", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                          })
                          ?.split("/")
                          .reverse()
                          .join("-"),
                      )
                    }
                    required={false}
                    error={errors.creditNoteDate?.message as string}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Controller
                name="clientId"
                control={control}
                defaultValue={null}
                rules={{ required: t("This field is required.") }}
                render={({ field }) => (
                  <DynamicSelect
                    label={t("Client") + " *"}
                    defaultPlaceholder="phSelectCustomer"
                    {...field}
                    options={clients}
                    isSearchable
                    onChange={(selectedOption: any) => {
                      setValue("invoiceId", null);
                      field.onChange(selectedOption);
                      fetchInvoicesByClient(selectedOption.value, 1, "");
                    }}
                    error={errors.clientId?.message as string}
                    isLoading={searchLoading === "customers"}
                    onInputChange={(
                      search: string,
                      { action }: { action: string },
                    ) => {
                      if (
                        action === "input-change" ||
                        (action !== "set-value" &&
                          action !== "menu-close" &&
                          action !== "input-blur")
                      )
                        handleDropdownActions("customers", "searchIt", search);
                      else if (
                        action === "input-blur" ||
                        action === "menu-close"
                      ) {
                        if (dropdownSearchTerm || pageNo > 1)
                          handleDropdownActions("customers", "searchIt", "");
                        else setDropdownSearchTerm("");
                        setPageNo(1);
                      }
                    }}
                    onMenuScrollToBottom={() => {
                      if (!(clientsData?.pages[0]?.data as any).last)
                        handleDropdownActions("customers", "loadNext");
                    }}
                    page={pageNo}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Controller
                name="invoiceId"
                control={control}
                defaultValue={null}
                rules={{ required: t("This field is required.") }}
                render={({ field }) => (
                  <DynamicSelect
                    label={t("invoices") + " *"}
                    defaultPlaceholder="phSelectInvoice"
                    {...field}
                    options={(invoices?.content || [])?.map(
                      (item: { id?: number; invoiceNo?: string }) => ({
                        value: item.id,
                        label: item.invoiceNo,
                      }),
                    )}
                    isSearchable
                    onChange={(selectedOption: any) => {
                      setLoading(true);
                      field.onChange(selectedOption);
                      setTimeout(
                        () => fetchInvoiceById(selectedOption.value),
                        700,
                      );
                    }}
                    error={errors.invoiceId?.message as string}
                    isDisabled={watch("clientId")?.value ? false : true}
                    isLoading={invoiceloader || searchLoading === "invoices"}
                    onInputChange={(
                      search: string,
                      { action }: { action: string },
                    ) => {
                      if (
                        action === "input-change" ||
                        (action !== "set-value" &&
                          action !== "menu-close" &&
                          action !== "input-blur")
                      )
                        handleDropdownActions("invoices", "searchIt", search);
                      else if (
                        action === "input-blur" ||
                        action === "menu-close"
                      ) {
                        if (dropdownSearchTerm || pageNo > 1)
                          handleDropdownActions("invoices", "searchIt", "");
                        else setDropdownSearchTerm("");
                        setPageNo(1);
                      }
                    }}
                    onMenuScrollToBottom={() => {
                      if (!invoices?.last)
                        handleDropdownActions("invoices", "loadNext");
                    }}
                    page={pageNo}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                {...register("reason", {
                  validate: {
                    required: (value) =>
                      value.trim() !== "" || t("This field is required."),
                  },
                })}
                label={t("remarks") + " *"}
                placeholder={t("phCreditNoteRemarks")}
                title={t("remarks")}
                fullWidth
                error={!!errors.reason}
                helperText={errors.reason?.message as string}
                InputLabelProps={{ shrink: true }}
                multiline
                rows={1}
                sx={{
                  "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                    width: "100%",
                    paddingRight: "0px",
                    paddingBottom: 0,
                  },
                  "& .MuiInputBase-inputMultiline": {
                    resize: "vertical",
                    minHeight: "29px",
                  },
                }}
              />
            </Grid>
            {watch("clientId")?.value && watch("invoiceId")?.value ? (
              <Box className="w-[100%]">
                <Box className="bg-white p-[15px] rounded-[8px] my-[20px] ms-[15px]">
                  {loading ? (
                    <Loader />
                  ) : (
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <SalesCreditNoteTable
                          rows={invoiceItem}
                          columns={[
                            { id: "itemId", label: t("Item"), numeric: false },
                            {
                              id: "quantity",
                              label: t("quantity"),
                              numeric: false,
                            },
                            {
                              id: "unitId",
                              label: t("unit(s)"),
                              numeric: false,
                            },
                            {
                              id: "unitPrice",
                              label: t("unitPrice"),
                              numeric: false,
                            },
                            {
                              id: "stockIn",
                              label: t("reStock"),
                              numeric: false,
                            },
                          ]}
                          DeleteItem={DeleteItem}
                          deletedItems={deletedItems}
                          maxItemCount={invoiceItemsLength}
                          form={{
                            register,
                            control,
                            handleSubmit,
                            errors,
                            watch,
                            setValue,
                          }}
                          handleInputChange={handleInputChange}
                          restoreDeletedItem={restoreDeletedItem}
                          themeColors={themeColors}
                        />
                      </Grid>
                      {invoiceItem.length < invoiceItemsLength && (
                        <Grid item xs={12}>
                          <Button
                            className="secondary flex gap-1 mt-[-5px]"
                            style={{ color: themeColors?.primary }}
                            variant="contained"
                            disableElevation
                            onClick={() => addNewItem()}
                          >
                            <AddIcon className="w-[20px] h-[20px]" />{" "}
                            {t("Add More Items")}
                          </Button>
                        </Grid>
                      )}
                    </Grid>
                  )}
                </Box>
                {loading ? (
                  <Loader />
                ) : (
                  <Grid container xs={12} spacing={1}>
                    <Grid item xs={12} md={6}></Grid>
                    <Grid item xs={12} md={6}>
                      <Box
                        className="bg-[#D8D1E0] rounded-[8px]"
                        sx={{
                          border: "1px solid #5f6770",
                          width: "calc(100% + 8px)",
                          padding: "8px 14px",
                        }}
                      >
                        <Box className="flex justify-end gap-12 mt-[9px]">
                          <Typography className="font-Saira text-[11px] text-[#3A3A3A] w-[auto]">
                            {t("subTotal")}
                          </Typography>
                          <Typography className="font-Saira text-[11px] text-[#3A3A3A] w-[20%] text-end">
                            <span className="uppercase">
                              {loggedInUserCurrency}
                            </span>{" "}
                            {new Intl.NumberFormat("en-US", {}).format(
                              calculation?.subtotalAmount || 0,
                            )}
                          </Typography>
                        </Box>
                        <Box className="flex justify-end gap-12 mt-[9px]">
                          <Typography className="font-Saira text-[11px] text-[#3A3A3A] w-[auto]">
                            {t("tax")}
                          </Typography>
                          <Typography className="font-Saira text-[11px] text-[#3A3A3A] w-[20%] text-end">
                            <span className="uppercase">
                              {loggedInUserCurrency}
                            </span>{" "}
                            {new Intl.NumberFormat("en-US", {}).format(
                              calculation?.taxAmount || 0,
                            )}
                          </Typography>
                        </Box>
                        <Box className="flex justify-end gap-12 mt-[9px]">
                          <Typography className="font-Saira text-[11px] text-[#3A3A3A] font-semibold w-[auto]">
                            {t("total")}
                          </Typography>
                          <Typography className="font-Saira text-[11px] text-[#3A3A3A] font-semibold w-[20%] text-end">
                            <span className="uppercase">
                              {loggedInUserCurrency}
                            </span>{" "}
                            {new Intl.NumberFormat("en-US", {}).format(
                              calculation?.totalAmount || 0,
                            )}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                )}
              </Box>
            ) : null}
          </Grid>
        }
      />
    </>
  );
}
